import React from 'react';

import { Box, Button } from '@mui/material';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeError } from '@stripe/stripe-js';

import { Form } from '@hrlogics/react-components/form';

import { SUPPORT_CLEARVERIFY_EMAIL } from '@clearverify/constants';

import { IS_PRODUCTION } from 'src/config';
import { useNotification } from 'src/hooks/app';
import { useScreenInfo } from 'src/hooks/common';
import { useModal } from 'src/hooks/shared';

interface StripeAddPaymentMethodFormProps {
  returnUrl: string;
  onCancel: () => void;
  switchClosability: ReturnType<typeof useModal>['switchClosability'];
}

export const StripeAddPaymentMethodForm: React.FC<StripeAddPaymentMethodFormProps> = ({
  returnUrl,
  onCancel,
  switchClosability,
}) => {
  const { spawnNotification } = useNotification();
  const { isMobile } = useScreenInfo();

  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = React.useCallback(async () => {
    if (!stripe || !elements) return;

    switchClosability(false);

    const res = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: returnUrl,
      },
    });

    if (res.error.message) {
      spawnNotification(res.error.message, 'error');
    }

    switchClosability(true);
    onCancel();
  }, [stripe, elements, switchClosability, returnUrl, onCancel, spawnNotification]);

  const handleElementsLoadError = React.useCallback(
    (event: { error: StripeError }) => {
      if (IS_PRODUCTION) {
        spawnNotification(
          `Stripe loading error. Please contact support: ${SUPPORT_CLEARVERIFY_EMAIL}`,
          'error',
        );
      } else {
        spawnNotification(event.error.message, 'error');
      }

      onCancel();
    },
    [onCancel, spawnNotification],
  );

  const isLoading = !stripe || !elements;

  return (
    <Form onSubmit={handleSubmit}>
      {({ submitting }) => (
        <React.Fragment>
          <PaymentElement onLoadError={handleElementsLoadError} />

          <Box display="flex" justifyContent={isMobile ? 'center' : 'space-between'} mt={2} gap={2}>
            <Button
              variant="outlined"
              color="error"
              onClick={onCancel}
              loading={submitting || isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" variant="contained" loading={submitting || isLoading}>
              Submit
            </Button>
          </Box>
        </React.Fragment>
      )}
    </Form>
  );
};
